import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Alert, Button, HStack, Separator, Text, VStack } from '@chakra-ui/react';
import { AdvisorProfileHubGate, AdvisorProfileLayout } from '@src/components/sco/Advisors/layout/AdvisorProfileLayout';
import { useAdvisorsAccess } from '@src/components/sco/Advisors/model/access';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { ProfileLinksSection } from '@src/components/sco/Advisors/sections/ProfileLinksSection';
import { useAdvisorApplication } from '@src/components/sco/Advisors/sections/advisorApplication/useAdvisorApplication';
import { AdvisorCompletenessCard } from '@src/components/sco/Advisors/shared/AdvisorCompletenessCard';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { SettingsFormSection } from '@src/components/sco/Advisors/shared/SettingsFormSection';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_PROFILE_STATUS_APPLIED, ADVISOR_PROFILE_STATUS_APPROVED, ADVISOR_PROFILE_STATUS_REJECTED, ADVISOR_PROFILE_STATUS_SUSPENDED } from '@src/constants/advisor-profile-statuses';
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useEffect, useState } from 'react';
type MyProfileData = {
  myAdvisorProfile?: ScoAdvisorProfile | null;
};
const PendingApplicationStatus = () => {
  const $ = _c(19);
  const paths = useAdvisorsPaths();
  const {
    canAutoApproveAdvisorApplication,
    wilUpgradeHref
  } = useAdvisorsAccess();
  const {
    resubmitForReview,
    saving,
    error,
    setError
  } = useAdvisorApplication();
  const [resubmitError, setResubmitError] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Alert.Indicator />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <VStack align="stretch" gap={1}><Text fontWeight="semibold">Application in review</Text><Text color="colorPalette.fg/70" fontFamily="longform" textStyle="sm">You will not appear in the directory until you are approved. Members on the $49+ plan skip this queue.</Text></VStack>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== error || $[3] !== resubmitError) {
    t2 = (resubmitError || error) && <Text color="red.fg" fontFamily="longform" textStyle="sm">{resubmitError || error}</Text>;
    $[2] = error;
    $[3] = resubmitError;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== canAutoApproveAdvisorApplication || $[6] !== resubmitForReview || $[7] !== saving || $[8] !== setError || $[9] !== wilUpgradeHref) {
    t3 = canAutoApproveAdvisorApplication ? <Button loading={saving} size="sm" variant="cta" onClick={() => {
      (async () => {
        setResubmitError(null);
        const result = await resubmitForReview();
        if (!result.ok) {
          return;
        }
        if (result.statusKey !== ADVISOR_PROFILE_STATUS_APPROVED) {
          setResubmitError("Your application is still in review. Refresh after your $49 plan is active, then try again.");
          setError("Your application is still in review. Refresh after your $49 plan is active, then try again.");
        }
      })();
    }}>Submit and go live</Button> : <Button asChild={true} size="sm" variant="cta"><NextLink href={wilUpgradeHref}>Upgrade to Startups Pro</NextLink></Button>;
    $[5] = canAutoApproveAdvisorApplication;
    $[6] = resubmitForReview;
    $[7] = saving;
    $[8] = setError;
    $[9] = wilUpgradeHref;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== paths.profileEdit) {
    t4 = <Button asChild={true} size="sm" variant="outline"><NextLink href={paths.profileEdit}>Update application</NextLink></Button>;
    $[11] = paths.profileEdit;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <HStack gap={2} wrap="wrap">{t3}{t4}</HStack>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t2 || $[17] !== t5) {
    t6 = <Alert.Root borderRadius="l2" status="warning">{t0}<VStack align="stretch" flex={1} gap={3} paddingLeft={2}>{t1}{t2}{t5}</VStack></Alert.Root>;
    $[16] = t2;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
const SetupStatus = t0 => {
  const $ = _c(5);
  const {
    statusKey
  } = t0;
  const paths = useAdvisorsPaths();
  if (statusKey === ADVISOR_PROFILE_STATUS_SUSPENDED) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <VStack align="stretch" gap={2} textAlign="center"><Text fontWeight="semibold">This advisor profile is suspended</Text><Text color="colorPalette.fg/70" fontFamily="longform">An admin will need to restore it before you can apply again.</Text></VStack>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (statusKey === ADVISOR_PROFILE_STATUS_REJECTED) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Text color="orange.fg" fontFamily="longform" fontSize="sm">Your previous application was not approved. Update it and resubmit.</Text>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== paths.profileEdit) {
      t2 = <VStack align="stretch" gap={3}>{t1}<Button asChild={true} alignSelf="flex-start" variant="cta"><NextLink href={paths.profileEdit}>Update application</NextLink></Button></VStack>;
      $[2] = paths.profileEdit;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (statusKey === ADVISOR_PROFILE_STATUS_APPLIED) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <PendingApplicationStatus />;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  return null;
};
const SetupBody = () => {
  const $ = _c(22);
  const {
    currentUser
  } = useCurrentUser();
  const t0 = !currentUser?.userId;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      skip: t0,
      fetchPolicy: "cache-and-network"
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const profileQuery = useQuery(MY_ADVISOR_PROFILE, t1);
  const myProfile = profileQuery.data?.myAdvisorProfile;
  const statusKey = myProfile?.statusKey ?? currentUser?.advisorProfile?.statusKey;
  const showSetup = statusKey !== ADVISOR_PROFILE_STATUS_SUSPENDED;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(_temp, t2);
  const t3 = profileQuery.loading && !myProfile;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Separator />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== statusKey) {
    t5 = <SetupStatus statusKey={statusKey} />;
    $[4] = statusKey;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== currentUser?.pictureUrl || $[7] !== myProfile || $[8] !== showSetup) {
    t6 = showSetup && <AdvisorCompletenessCard foundersHelpedCount={myProfile?.foundersHelpedCount} pictureUrl={currentUser?.pictureUrl} profile={myProfile} responsesCount={myProfile?.responsesCount} />;
    $[6] = currentUser?.pictureUrl;
    $[7] = myProfile;
    $[8] = showSetup;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <VStack align="stretch" gap={6}>{t5}{t6}</VStack>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== showSetup) {
    t8 = showSetup && <SettingsFormSection description="Share these addresses so founders can find you and book." id="links" title="Profile links"><ProfileLinksSection /></SettingsFormSection>;
    $[13] = showSetup;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <VStack align="stretch" gap={10} separator={t4}>{t7}{t8}</VStack>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== profileQuery.error || $[19] !== t3 || $[20] !== t9) {
    t10 = <AdvisorsQueryState error={profileQuery.error} loading={t3}>{t9}</AdvisorsQueryState>;
    $[18] = profileQuery.error;
    $[19] = t3;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};

/** `/advisors/profile` and `/advisors/profile/setup` — readiness, application status, and share links. */
export const AdvisorProfileSetupSection = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <AdvisorProfileHubGate><AdvisorProfileLayout compactHeader={true} activeKey="setup" description="Finish profile, rates, then Connect." title="Setup"><SetupBody /></AdvisorProfileLayout></AdvisorProfileHubGate>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp() {
  if (typeof window === "undefined") {
    return;
  }
  const id = window.location.hash.replace(/^#/, "");
  if (!id) {
    return;
  }
  let tries = 0;
  let timer = 0;
  const tick = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({
        block: "start"
      });
      return;
    }
    if (tries < 20) {
      tries = tries + 1;
      tries;
      timer = window.setTimeout(tick, 100);
    }
  };
  tick();
  return () => window.clearTimeout(timer);
}